import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { FaSortAlphaDown, FaSortAlphaUp, FaDollarSign } from 'react-icons/fa';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import ARNavBar from './ARNavBar';
import ArHistorySearch from './components/ArHistorySearch';
import ArHistoryInvoice from './models/arHistoryInvoice';
import ArHistoryPaymentHistoryDialog from './dialogs/ArHistoryPaymentHistoryDialog';
import ArHistoryReportDialog, { IArHistoryReportParams } from './dialogs/ArHistoryReportDialog';
import NoSelectedInvoicesWarningDialog from 'src/components/accounts-payable/dialogs/NoSelectedInvoicesWarningDialog';
import { IArHistorySearchCriteria, useArServiceCtx } from './AccountsReceivableServiceContext';
import './ArHistoryPage.module.scss';

export const formatDateForDisplay = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

export const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Mirrors oid 314's live PDF path (Run_Report_To_File -> Download_AS_File), backed by
// ARHistoryReport.rdl/usp_ARHistoryReport. begDate/endDate come through as YYYY-MM-DD from the
// <input type="date"> field - the RDL's own params are typed String and TRY_CAST inside the proc
// (see the SSRS gotchas memory on why: SSRS parses DateTime params using its own server locale),
// so no reformatting is needed here.
export const buildArHistoryReportUrl = (criteria: IArHistorySearchCriteria, params: IArHistoryReportParams): string => {
    const query = new URLSearchParams({ orderBy: params.orderBy });
    const addIfPresent = (key: string, value: string | number | undefined) => {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    };
    addIfPresent('clNo', criteria.clientNumber);
    addIfPresent('prNo', criteria.projectNumber);
    addIfPresent('invoiceType', criteria.invoiceType);
    addIfPresent('begDate', criteria.begDate);
    addIfPresent('endDate', criteria.endDate);
    addIfPresent('invoiceStatus', criteria.invoiceStatus);
    addIfPresent('balance', params.balance);
    return `/v1.0/reports/GetReport/ARHistoryReport?${query.toString()}`;
};

// usp_ARHistory (backing /LMSBILLING/ARHistory) parses beg_date/end_date with
// CONVERT(DATE, @beg_date, 101) - an explicit MM/DD/YYYY style, not the locale-safe TRY_CAST
// usp_ARHistoryReport (the PDF report) uses. The <input type="date"> field hands back ISO
// YYYY-MM-DD, which that CONVERT rejects - a real SQL error that ExecuteReportRequest masks into
// a 204 (see the SSRS gotchas memory), surfacing as a false "No invoices matched" instead of the
// actual conversion failure. Reformat to MM/DD/YYYY here so the Excel report gets what its own
// proc actually expects.
const toMMDDYYYY = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
};

// Mirrors oid 344's live Excel OK button (XL_AR_History -> Download_AS_File), backed by a
// separate, already-deployed report (/LMSBILLING/ARHistory, not ARHistoryReport) with its own
// smaller parameter set - no invoiceType or balance, since that report doesn't take either.
export const buildArHistoryExcelReportUrl = (criteria: IArHistorySearchCriteria, params: IArHistoryReportParams): string => {
    const query = new URLSearchParams({ orderBy: params.orderBy });
    const addIfPresent = (key: string, value: string | number | undefined) => {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    };
    addIfPresent('clNo', criteria.clientNumber);
    addIfPresent('prNo', criteria.projectNumber);
    addIfPresent('begDate', criteria.begDate ? toMMDDYYYY(criteria.begDate) : undefined);
    addIfPresent('endDate', criteria.endDate ? toMMDDYYYY(criteria.endDate) : undefined);
    addIfPresent('invoiceStatus', criteria.invoiceStatus);
    return `/v1.0/reports/GetReport/ARHistoryExcelReport?${query.toString()}`;
};

type SortField = 'client' | 'project' | 'referenceNumber' | 'amount' | 'transactionDate';

// Multi-key comparators mirror the Oracle Query buttons' own order_by strings (oid 58/61/64/67/70).
const SORT_COMPARATORS: Record<SortField, (a: ArHistoryInvoice, b: ArHistoryInvoice) => number> = {
    client: (a, b) => (a.clientNumber - b.clientNumber) || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    project: (a, b) => (a.projectNumber - b.projectNumber) || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    referenceNumber: (a, b) => (a.referenceNumber ?? '').localeCompare(b.referenceNumber ?? '') || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    amount: (a, b) => (a.amount - b.amount) || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    transactionDate: (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime(),
};

const ArHistoryPage = () => {
    const dispatch = useDispatch();
    const { getArHistoryInvoices } = useArServiceCtx();
    // Separate instance so this GET doesn't race/abort against the grid's own fetch on the
    // service context's shared instance (same reasoning as CheckRegisterPage.tsx's getOffices).
    const { get: getReport, response: reportResponse } = useFetch();
    const [invoices, setInvoices] = useState<Array<ArHistoryInvoice>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [sortField, setSortField] = useState<SortField | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    const [selectedEntryNumber, setSelectedEntryNumber] = useState<number | undefined>(undefined);
    // Last-run search criteria - the Report dialog reuses these (mirrors :control.* being read
    // directly off the Control block in oid 52) alongside its own Order By/Balance Status fields.
    const [lastCriteria, setLastCriteria] = useState<IArHistorySearchCriteria>({ invoiceType: '3' });
    const [reportMessage, setReportMessage] = useState<string>('');
    const [reportGenerating, setReportGenerating] = useState<boolean>(false);
    // Unbounded usp_ARHistoryReport runs against all of AR_DETAIL (542k+ rows with no filters at
    // all) - the same class of problem the SSRS gotchas memory warns about (a query with no upper
    // bound can hang the report server rather than just running slow). Requires at least one of
    // Client No./Prospect No./a full date range before "Run Report" is allowed to fire.
    const [reportRequirementsMessage, setReportRequirementsMessage] = useState<string>('');

    const runQuery = async (criteria: IArHistorySearchCriteria) => {
        setLoading(true);
        setLastCriteria(criteria);
        const result = await getArHistoryInvoices(criteria);
        setInvoices(Array.isArray(result) ? result : []);
        setLoading(false);
    };

    const handleSort = (field: SortField) => {
        setSortField(field);
        setSortDescending(field === sortField ? !sortDescending : false);
    };

    const sortedInvoices = React.useMemo(() => {
        if (!sortField) {
            return invoices;
        }
        const sorted = [...invoices].sort(SORT_COMPARATORS[sortField]);
        return sortDescending ? sorted.reverse() : sorted;
    }, [invoices, sortField, sortDescending]);

    const renderSortableHeader = (label: string, field: SortField, style?: React.CSSProperties) => {
        const willSortDescending = field === sortField ? !sortDescending : false;
        return (
            <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', ...style }}
                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                onClick={() => handleSort(field)}
            >
                {label}
                <Button
                    style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                    text={''}
                    reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                    iconHoverColor="blue"
                    iconStyle={{ width: '1.25rem', height: '1.25rem', verticalAlign: 'middle' }}
                />
            </th>
        );
    };

    // Mirrors oid 52's Go_Block('Standard') -> opens the Report popup (canvas C_STANDARD /
    // window W_STANDARD). Gated before it even opens - see reportRequirementsMessage above for
    // why - rather than letting the user pick Order By/Balance/Format first and only then
    // finding out the query has no upper bound.
    const handleReport = () => {
        const hasClient = lastCriteria.clientNumber !== undefined;
        const hasProject = lastCriteria.projectNumber !== undefined;
        const hasDateRange = !!lastCriteria.begDate && !!lastCriteria.endDate;
        if (!hasClient && !hasProject && !hasDateRange) {
            setReportRequirementsMessage('Please specify a Client No., a Prospect No., or a Beg Date and End Date, then click Search, before running the report.');
            dispatch(actions.layout.showDialog(DialogList.AR_HISTORY_REPORT_REQUIREMENTS));
            return;
        }

        setReportMessage('');
        dispatch(actions.layout.showDialog(DialogList.AR_HISTORY_REPORT));
    };

    // Mirrors oid 314's live PDF OK button (Run_Report_To_File -> Download_AS_File ->
    // web.Show_Document) and oid 344's live Excel OK button (XL_AR_History -> Download_AS_File) -
    // one handler covering both since ArHistoryReportDialog's toggle picks the format. Both hit
    // already-deployed, separate SSRS reports/procs - this only builds the right URL per format.
    const handleGenerateReport = async (params: IArHistoryReportParams) => {
        setReportGenerating(true);
        setReportMessage('');

        const isExcel = params.format === 'EXCEL';
        const url = isExcel ? buildArHistoryExcelReportUrl(lastCriteria, params) : buildArHistoryReportUrl(lastCriteria, params);
        await getReport(url);

        if (!reportResponse.ok) {
            setReportMessage('Unable to generate the report. Please try again.');
            setReportGenerating(false);
            return;
        }

        if (reportResponse.status === 204) {
            setReportMessage('No invoices matched those filters.');
            setReportGenerating(false);
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = isExcel ? 'AR-History.xlsx' : 'AR-History.pdf';
        a.click();
        window.URL.revokeObjectURL(blobUrl);

        setReportGenerating(false);
        hideDialog();
    };

    // Mirrors oid 114's WHEN-MOUSE-DOUBLECLICK -> Go_Block('Ar_Detail_Trans'), opening the
    // Payments popup for this invoice's payment history.
    const handleRowDoubleClick = (item: ArHistoryInvoice) => {
        setSelectedEntryNumber(item.entryNumber);
        dispatch(actions.layout.showDialog(DialogList.AR_HISTORY_PAYMENTS));
    };
    const hideDialog = () => dispatch(actions.layout.hideDialog());

    return (
        <div>
            <ARNavBar />
            <ArHistorySearch runQuery={runQuery} />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="A/R History">
                            <div className="form-row" style={{ justifyContent: 'center', marginBottom: '.5rem' }}>
                                <Button className="blue-hover-button" style={{ width: '7rem', height: '3rem' }} text="Report" onClick={handleReport} />
                            </div>
                            <div style={{ overflowY: 'scroll', maxHeight: '32rem' }}>
                                <table style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            {renderSortableHeader('Client', 'client', { width: '7%' })}
                                            {renderSortableHeader('Pr. No.', 'project', { width: '7%' })}
                                            {renderSortableHeader('Invoice No.', 'referenceNumber', { width: '9%' })}
                                            <th style={{ width: '19%' }}>Description</th>
                                            {renderSortableHeader('Inv. Amount', 'amount', { width: '9%' })}
                                            {renderSortableHeader('Inv. Date', 'transactionDate', { width: '9%' })}
                                            <th style={{ width: '10%' }}>Paid By</th>
                                            <th style={{ width: '4%' }}>#</th>
                                            <th style={{ width: '9%' }}>Paid Date</th>
                                            <th style={{ width: '9%' }}>Amount Paid</th>
                                            <th style={{ width: '8%' }}>Balance</th>
                                            <th style={{ width: '4%' }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr>
                                                <td colSpan={12} className="bold-centered">
                                                    <LoadingSpinner />
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && sortedInvoices.map((item, index) => (
                                            <tr
                                                key={`${item.entryNumber}-${index}`}
                                                className="table-item-hover"
                                                style={{ textAlign: 'center', cursor: 'pointer' }}
                                                onDoubleClick={() => handleRowDoubleClick(item)}
                                                title="Double-click for payment history"
                                            >
                                                <td className="arHistoryCell">{item.clientNumber}</td>
                                                <td className="arHistoryCell">{item.projectNumber}</td>
                                                <td className="arHistoryCell">{item.referenceNumber}</td>
                                                <td className="arHistoryCell" style={{ textAlign: 'left' }}>{item.description}</td>
                                                <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                                                <td className="arHistoryCell">{formatDateForDisplay(item.transactionDate)}</td>
                                                <td className="arHistoryCell">{item.paidByCheckNo}</td>
                                                <td className="arHistoryCell">{item.transactionCount || ''}</td>
                                                <td className="arHistoryCell">{formatDateForDisplay(item.paidDate)}</td>
                                                <td className="arHistoryCell" style={{ textAlign: 'right' }}>{item.amountPaid != null ? formatCurrency(item.amountPaid) : ''}</td>
                                                <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(item.balance)}</td>
                                                <td className="arHistoryCell" onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        reactIcon={FaDollarSign}
                                                        iconStyle={{ fill: 'green', width: '1.5rem', height: '1.5rem', verticalAlign: 'middle' }}
                                                        iconHoverColor="blue"
                                                        title="Payment History"
                                                        text=""
                                                        style={{ height: '2.5rem', background: 'transparent' }}
                                                        onClick={() => handleRowDoubleClick(item)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {!loading && invoices.length === 0 && (
                                            <tr>
                                                <td colSpan={12} className="bold-centered">No Invoices Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <ArHistoryPaymentHistoryDialog
                dialogName={DialogList.AR_HISTORY_PAYMENTS}
                hideDialog={hideDialog}
                entryNumber={selectedEntryNumber}
            />
            <ArHistoryReportDialog
                dialogName={DialogList.AR_HISTORY_REPORT}
                hideDialog={hideDialog}
                onGenerateReport={handleGenerateReport}
                message={reportMessage}
                generating={reportGenerating}
            />
            <NoSelectedInvoicesWarningDialog
                dialogName={DialogList.AR_HISTORY_REPORT_REQUIREMENTS}
                hideDialog={hideDialog}
                heading="Report Filters Required"
                label={reportRequirementsMessage}
            />
        </div>
    );
};

export default ArHistoryPage;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/ArHistoryPage.tsx